import { useState, useEffect, useRef, ChangeEvent } from 'react';
import {
  PlusOutlined,
  SearchOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { NewLoad } from 'Components/NewLoad/NewLoad';
import { Button, Segmented, Input, Modal, Tabs, Spin } from 'antd';
import { Legacy } from './Legacy';
import { Contact } from 'Components/Contact/Contact';
import { Header } from 'Components/Chat/Header/Header';
import { MessagesList } from 'Components/Chat/MessagesList/MessagesList';
import { useAppDispatch } from 'Redux/store';
import { getChatsList, getChatMessages } from 'Redux/Chat/reducer';
import { useSelector } from 'react-redux';
import {
  selectChatsList,
  selectChatMessages,
  filterChatsList,
  resetChatMessages,
  selectChatLoading,
} from 'Redux/Chat/slice';
import { IPrivateChat, IGroupChat } from 'Shared/Types/chat';
import styles from './Chat.module.scss';
import legacyStyles from './Legacy.module.scss';

export const Chat = () => {
  const [isGroupChats, setIsGroupChats] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeChat, setActiveChat] = useState<
    IPrivateChat | IGroupChat | null
    >(null);
  const [createGroupModalIsOpen, setCreateGroupModalIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const chatsList = useSelector(selectChatsList);
  const chatMessages = useSelector(selectChatMessages);
  const isLoading = useSelector(selectChatLoading);
  const listRef = useRef<HTMLDivElement>(null);

  const toggleChatsType = () => {
    setIsGroupChats((prevState) => !prevState);
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    setSearchInput(searchTerm);

    dispatch(filterChatsList(searchTerm));
  };

  const handleContactClick = (chat: IPrivateChat | IGroupChat) => {
    setActiveChat(chat);
    dispatch(
      getChatMessages({
        category: isGroupChats ? 'group' : 'private',
        chatID: chat.id,
      }),
    );
  };

  const getDefaultMessage = ({
    chatListIsEmpty,
    isActiveChat,
    isGroupChats,
  }: {
    chatListIsEmpty: boolean;
    isActiveChat: boolean;
    isGroupChats: boolean;
  }): string | undefined => {
    if (chatListIsEmpty === false && isActiveChat === false) {
      return `Select a ${
        isGroupChats ? 'group' : 'private'
      } chat to start messaging.`;
    }

    if (chatListIsEmpty && isGroupChats) {
      return 'To start chatting, please create a chat group.';
    }

    if (chatListIsEmpty) {
      return 'To start chatting, please create a user.';
    }
  };

  useEffect(() => {
    dispatch(getChatsList('private'));
  }, []);

  useEffect(() => {
    if (isGroupChats) {
      dispatch(getChatsList('group'));
    } else {
      dispatch(getChatsList('private'));
    }
    setActiveChat(null);
    dispatch(resetChatMessages());
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0 });
    }
  }, [isGroupChats, dispatch]);

  // useEffect(() => {
  //   setActiveChat(chatsList[0]);
  //   dispatch(
  //     getChatMessages({
  //       category: 'private',
  //       chatID: 123,
  //     }),
  //   );
  // }, [chatsList]);

  return (
    // <Legacy />
    <div className={styles.chat}>
      <div className={styles.chat__body}>
        <div className={styles.sidebar}>
          {isGroupChats && (
            <UsergroupAddOutlined
              className={styles.sidebar__addGroupButton}
              title="Add a new group"
              onClick={() => setCreateGroupModalIsOpen(true)}
            />
          )}
          <Segmented
            className={styles.sidebar__switcher}
            options={['Private', 'Group']}
            onChange={toggleChatsType}
            defaultValue={'Private'}
          />
          <Input
            className={styles.sidebar__searchInput}
            value={searchInput}
            size="large"
            placeholder="Search"
            prefix={<SearchOutlined />}
            onChange={handleSearchInputChange}
            disabled={Boolean(chatsList.length) === false}
            title={
              Boolean(chatsList.length) === false
                ? "You can't use Search while chat list is empty."
                : ''
            }
            allowClear
          />
          <div className={styles.sidebar__scrollbarWrapper}>
            <div className={styles.sidebar__list} ref={listRef}>
              {chatsList.map((chat: IPrivateChat | IGroupChat) => (
                <Contact
                  key={chat.id}
                  name={chat.name}
                  isActive={activeChat?.id === chat.id}
                  clickHandler={() => handleContactClick(chat)}
                  isGroup={isGroupChats}
                  message={chat.last_message || undefined}
                  timestamp={chat.last_timestamp || undefined}
                  avatarURL={'avatar' in chat ? chat.avatar : undefined}
                  userRole={'user_role' in chat ? chat.user_role : undefined}
                />
              ))}
            </div>
          </div>
        </div>

        <div
          className={`${styles.chat__main} ${
            activeChat ? styles.chat__main_active : styles.chat__main_default
          }`}
        >
          {isLoading || Boolean(chatsList.length) === false ? (
            <Spin className={styles.chat__main__spinner} size="large" />
          ) : (
            <div className={styles.chat__main__defaultInfo}>
              {(activeChat === null || Boolean(chatsList.length) === false) && (
                <span>
                  {getDefaultMessage({
                    chatListIsEmpty: Boolean(chatsList.length) === false,
                    isActiveChat: activeChat !== null,
                    isGroupChats,
                  })}
                </span>
              )}
              {isGroupChats && Boolean(chatsList.length) === false && (
                <button
                  className={styles.chat__main__createGroupButton}
                  onClick={() => setCreateGroupModalIsOpen(true)}
                >
                  <PlusOutlined />
                  Create Group
                </button>
              )}
            </div>
          )}
          {activeChat && <Header activeChat={activeChat} />}
          {activeChat && chatMessages && (
            <MessagesList messages={chatMessages} isGroup={isGroupChats} />
          )}
        </div>
      </div>

      <Modal
        className={legacyStyles.createGroupModal}
        open={createGroupModalIsOpen}
        footer={null}
        title={<p className={legacyStyles.detailTitle}>Create Group</p>}
        closable={true}
        maskClosable={true}
        onCancel={() => setCreateGroupModalIsOpen(false)}
        width={500}
      >
        <label className={legacyStyles.groupNameLabel} htmlFor="name">
          Group Name
        </label>
        <Input className={legacyStyles.groupNameInput} />
        <Button className={legacyStyles.uploadBtn}>Upload photo</Button>
        <div className={legacyStyles.lastBtns}>
          <Button className={legacyStyles.cancelBtn}>Cancel</Button>
          <Button className={legacyStyles.createGroupBtn}>Create Group</Button>
        </div>
      </Modal>
    </div>
  );
};
